import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../../main/baseQueryWithReauth';
import { attendeeEventsApi } from '../events/attendee-events.api';
import { attendeeFavoritesApi } from '../favorites/attendee-favorites.api';
import { aiAgentApi } from '../../type-partner/ai-agent/ai-agent.api';
import type { Thread } from '../../type-partner/ai-agent/models';
import type {
  AttendeeMeeting,
  AttendeeMeetingGroup,
  BookMeetingRequest,
  ChangeMeetingStatusRequest,
  GetAttendeeMeetingsResponse,
  GetAttendeeSlotsResponse,
  ChangeMatchStatusRequest,
  GetAttendeeChatsResponse,
  UpdateAttendeeChatRequest,
  CompanyOption,
  GetAttendeeNotesResponse,
  GetMatchesParams,
  GetMatchesResponse,
  PartnerMatchDetails,
  MatchIntentRequest,
  MatchIntentResponse,
  PartnerMatchItem,
} from './models';
import type { GetBriefsResponse, FillBriefAnswerPayload } from '../../type-partner/briefs/models';

const attendee_match = 'attendee_match';

/** AttendeeMeetingGroup on the backend — the tabs' ids */
const MEETING_GROUP_ID: Record<AttendeeMeetingGroup, number> = {
  all: 1,
  waiting: 2,
  confirmed: 3,
  past: 4,
};

export const attendeeMatchApi = createApi({
  reducerPath: attendee_match,
  baseQuery: baseQueryWithReauth,
  tagTypes: [attendee_match],
  endpoints: (build) => ({
    postMatchIntent: build.mutation<MatchIntentResponse, MatchIntentRequest>({
      query: (body) => ({
        url: '/attendee/match-intent',
        method: 'POST',
        body,
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      }),
      invalidatesTags: [{ type: attendee_match, id: 'LIST' }],
    }),
    // Paginated, and narrowed by the caller to what it renders: the flow asks
    // for undecided cards, the shortlist for the kept ones. A surface never
    // downloads the companies it is not showing — with a thousand processed
    // matches that was the whole history, every time. `summary` carries the
    // counters the pages used to compute off the full list.
    getMatches: build.query<GetMatchesResponse, GetMatchesParams | void>({
      query: (args) => {
        const params = new URLSearchParams();
        (args?.events ?? []).forEach((id) => params.append('filters[events][]', id));
        (args?.statuses ?? []).forEach((status) =>
          params.append('filters[statuses][]', String(status)),
        );
        if (args?.page) params.append('page', String(args.page));
        if (args?.per_page) params.append('per_page', String(args.per_page));
        const search = params.toString();

        return {
          url: `/attendee/matches${search ? `?${search}` : ''}`,
          method: 'GET',
          headers: { Accept: 'application/json' },
        };
      },
      // pages of the same filter accumulate into one list, so "load more" is a
      // page bump. The page number is not part of the cache key; everything
      // that changes WHAT is listed is
      serializeQueryArgs: ({ queryArgs, endpointName }) =>
        [
          endpointName,
          (queryArgs?.events ?? []).join(','),
          (queryArgs?.statuses ?? []).join(','),
          queryArgs?.per_page ?? '',
        ].join('|'),
      merge: (cache, incoming) => {
        // page 1 is the authority: a refetch after a decision replaces the list
        if (incoming.meta.current_page === 1) {
          return incoming;
        }

        const byId = new Map(cache.data.map((match) => [match.id, match]));
        incoming.data.forEach((match) => byId.set(match.id, match));

        return { ...incoming, data: [...byId.values()] };
      },
      forceRefetch: ({ currentArg, previousArg }) => currentArg !== previousArg,
      providesTags: [{ type: attendee_match, id: 'LIST' }],
    }),
    // one card by id — for a surface holding a match id and no list: the
    // recorded decisions in the matching feed, a company opened from a link
    getMatch: build.query<{ data: PartnerMatchDetails }, { id: number }>({
      query: ({ id }) => ({
        url: `/attendee/matches/${id}`,
        method: 'GET',
        headers: { Accept: 'application/json' },
      }),
      providesTags: (_result, _error, { id }) => [{ type: attendee_match, id }],
    }),

    // opens (or returns) the card's concierge chat — one thread per company;
    // an optional typed question rides along and is answered in the thread
    openMatchThread: build.mutation<{ data: Thread }, { matchId: number; message?: string }>({
      query: ({ matchId, message }) => ({
        url: `/attendee/matches/${matchId}/thread`,
        method: 'POST',
        body: message ? { message } : {},
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      }),
      // a freshly opened (or restored from the archive) company chat joins
      // the rail's Chats
      invalidatesTags: [{ type: attendee_match, id: 'CHATS' }],
      // the thread lives in the shared ai_agent slice — refresh its list so
      // the Chats page picks a freshly created thread up
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(aiAgentApi.util.invalidateTags([{ type: 'ai_agent', id: 'LIST' }]));
        } catch {
          // the mutation failed — nothing to refresh
        }
      },
    }),
    // opens (or returns) the general concierge thread — free-text questions
    // that name no company; the message is required, the thread starts from it
    openConciergeThread: build.mutation<{ data: Thread }, { message: string }>({
      query: ({ message }) => ({
        url: '/attendee/concierge/thread',
        method: 'POST',
        body: { message },
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      }),
      invalidatesTags: [{ type: attendee_match, id: 'CHATS' }],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(aiAgentApi.util.invalidateTags([{ type: 'ai_agent', id: 'LIST' }]));
        } catch {
          // the mutation failed — nothing to refresh
        }
      },
    }),
    // the "Add company" typeahead: partners of one of the attendee's events,
    // each with the status of the attendee's existing match card (if any)
    searchShortlistCompanies: build.query<
      { data: CompanyOption[] },
      { event_id: number; search?: string }
    >({
      query: ({ event_id, search }) => {
        const params = new URLSearchParams({ event_id: String(event_id) });
        if (search) params.append('search', search);

        return {
          url: `/attendee/matches/companies?${params.toString()}`,
          method: 'GET',
          headers: { Accept: 'application/json' },
        };
      },
      providesTags: [{ type: attendee_match, id: 'LIST' }],
    }),
    // shortlists a company onto one of the attendee's events: an existing
    // partner of the event by id, or a manual add (name/website) — then the
    // backend creates a shadow partner with a registration invite for its
    // known contact. Either way a regular, already-liked match card returns
    addShortlistCompany: build.mutation<
      { data: PartnerMatchItem },
      { event_id: number; partner_id?: number; name?: string; website?: string }
    >({
      query: (body) => ({
        url: '/attendee/matches/companies',
        method: 'POST',
        body,
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      }),
      invalidatesTags: [{ type: attendee_match, id: 'LIST' }],
      // the created card arrives already liked — a favorite row and the
      // per-event shortlist counters change with it
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            attendeeFavoritesApi.util.invalidateTags([{ type: 'attendee_favorites', id: 'LIST' }]),
          );
          dispatch(
            attendeeEventsApi.util.invalidateTags([{ type: 'attendee_events', id: 'LIST' }]),
          );
        } catch {
          // the mutation failed — nothing to refresh
        }
      },
    }),
    changeMatchStatus: build.mutation<{ data: PartnerMatchItem }, ChangeMatchStatusRequest>({
      query: ({ matchId, status, transition, skip_reason, skip_note }) => ({
        url: `/attendee/matches/${matchId}/status/${status}`,
        method: 'PATCH',
        body: {
          transition,
          ...(skip_reason ? { skip_reason } : {}),
          ...(skip_note ? { skip_note } : {}),
        },
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      }),
      // the list, and the card itself — the company page reads it by id
      invalidatesTags: (_result, _error, { matchId }) => [
        { type: attendee_match, id: 'LIST' },
        { type: attendee_match, id: matchId },
      ],
      // a like mirrors into favorites (and an events row) on the backend —
      // refresh those slices so the Shortlist/Events pages don't serve stale caches
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            attendeeFavoritesApi.util.invalidateTags([{ type: 'attendee_favorites', id: 'LIST' }]),
          );
          dispatch(
            attendeeEventsApi.util.invalidateTags([{ type: 'attendee_events', id: 'LIST' }]),
          );
        } catch {
          // the mutation failed — nothing to refresh
        }
      },
    }),
    // the per-event matching feed thread — matching is strictly per event,
    // each attendee event carries its own thread the /matching page renders
    openMatchingThread: build.mutation<{ data: Thread }, { event_id: number; message?: string }>({
      query: (body) => ({
        url: '/attendee/matching/thread',
        method: 'POST',
        body,
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      }),
    }),
    // the attendee's answer to a question the matching feed asked: saved into
    // the brief, recorded in the feed with the concierge's receipt
    answerMatchingQuestion: build.mutation<
      { data: Thread },
      { event_id: number; question: number; answer: string }
    >({
      query: (body) => ({
        url: '/attendee/matching/answer',
        method: 'POST',
        body,
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      }),
      invalidatesTags: [{ type: attendee_match, id: 'BRIEF' }],
    }),
    getAttendeeBrief: build.query<GetBriefsResponse, void>({
      query: () => ({
        url: '/attendee/brief',
        method: 'GET',
        headers: { Accept: 'application/json' },
      }),
      providesTags: [{ type: attendee_match, id: 'BRIEF' }],
    }),
    fillAttendeeBrief: build.mutation<GetBriefsResponse, { answers: FillBriefAnswerPayload[] }>({
      query: (body) => ({
        url: '/attendee/brief',
        method: 'PATCH',
        body,
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      }),
      invalidatesTags: [{ type: attendee_match, id: 'BRIEF' }],
    }),
    // concierge notes on the own organization — durable facts the agent
    // learned in chats; every mutation answers with the full remaining set
    getAttendeeNotes: build.query<GetAttendeeNotesResponse, void>({
      query: () => ({
        url: '/attendee/notes',
        method: 'GET',
        headers: { Accept: 'application/json' },
      }),
      providesTags: [{ type: attendee_match, id: 'NOTES' }],
    }),
    updateAttendeeNote: build.mutation<GetAttendeeNotesResponse, { id: string; note: string }>({
      query: ({ id, note }) => ({
        url: `/attendee/notes/${id}`,
        method: 'PATCH',
        body: { note },
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      }),
      invalidatesTags: [{ type: attendee_match, id: 'NOTES' }],
    }),
    deleteAttendeeNote: build.mutation<GetAttendeeNotesResponse, { id: string }>({
      query: ({ id }) => ({
        url: `/attendee/notes/${id}`,
        method: 'DELETE',
        headers: { Accept: 'application/json' },
      }),
      invalidatesTags: [{ type: attendee_match, id: 'NOTES' }],
    }),
    // the free slots of the sponsor's published hours for one card's event
    getMatchSlots: build.query<GetAttendeeSlotsResponse, { matchId: number }>({
      query: ({ matchId }) => ({
        url: `/attendee/matches/${matchId}/slots`,
        method: 'GET',
        headers: { Accept: 'application/json' },
      }),
      providesTags: (_result, _error, { matchId }) => [
        { type: attendee_match, id: `SLOTS-${matchId}` },
      ],
    }),
    // book one slot: the card becomes a meeting request with that time
    bookMeeting: build.mutation<{ data: AttendeeMeeting }, BookMeetingRequest>({
      query: ({ matchId, ...body }) => ({
        url: `/attendee/matches/${matchId}/meeting`,
        method: 'POST',
        body,
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      }),
      invalidatesTags: (_result, _error, { matchId }) => [
        { type: attendee_match, id: 'LIST' },
        { type: attendee_match, id: matchId },
        { type: attendee_match, id: `SLOTS-${matchId}` },
        { type: attendee_match, id: 'MEETINGS' },
      ],
    }),
    // the Meetings page: one group at a time, the counts of every group beside
    getAttendeeMeetings: build.query<
      GetAttendeeMeetingsResponse,
      { events?: string[]; group?: AttendeeMeetingGroup; page?: number } | void
    >({
      query: (args) => {
        const params = new URLSearchParams();
        (args?.events ?? []).forEach((id) => params.append('filters[events][]', id));
        if (args?.group) params.append('filters[group]', String(MEETING_GROUP_ID[args.group]));
        if (args?.page) params.append('page', String(args.page));
        const search = params.toString();

        return {
          url: `/attendee/meetings${search ? `?${search}` : ''}`,
          method: 'GET',
          headers: { Accept: 'application/json' },
        };
      },
      providesTags: [{ type: attendee_match, id: 'MEETINGS' }],
    }),
    // the attendee's answer on a meeting: accept or decline an offer, withdraw a request
    changeMeetingStatus: build.mutation<{ data: AttendeeMeeting }, ChangeMeetingStatusRequest>({
      query: ({ meetingId, status, transition }) => ({
        url: `/attendee/meetings/${meetingId}/status/${status}`,
        method: 'PATCH',
        body: { transition },
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      }),
      invalidatesTags: [
        { type: attendee_match, id: 'MEETINGS' },
        { type: attendee_match, id: 'LIST' },
      ],
    }),
    // the rail's Chats: company conversations and free topics, newest first;
    // the archive is the same list with the archived flag
    // paginated: pages accumulate under one cache entry per list (live or
    // archived), so the rail and the Chats page scroll the same list
    getAttendeeChats: build.query<
      GetAttendeeChatsResponse,
      { archived?: boolean; page?: number } | void
    >({
      query: (args) => ({
        url: '/attendee/chats',
        method: 'GET',
        params: {
          page: args?.page ?? 1,
          ...(args?.archived && { 'filters[archived]': 1 }),
        },
        headers: { Accept: 'application/json' },
      }),
      serializeQueryArgs: ({ queryArgs }) =>
        `getAttendeeChats-${queryArgs?.archived ? 'archived' : 'live'}`,
      merge: (currentCache, newResponse) => {
        if (newResponse.meta.current_page === 1) {
          return newResponse;
        }
        const existingIds = new Set(currentCache.data.map((chat) => chat.id));
        return {
          ...newResponse,
          data: [
            ...currentCache.data,
            ...newResponse.data.filter((chat) => !existingIds.has(chat.id)),
          ],
        };
      },
      forceRefetch: ({ currentArg, previousArg }) => currentArg?.page !== previousArg?.page,
      providesTags: [{ type: attendee_match, id: 'CHATS' }],
    }),
    // the rail's "+": a new topic chat of its own, waiting for the first message
    startTopicChat: build.mutation<{ data: Thread }, { message?: string } | void>({
      query: (args) => ({
        url: '/attendee/concierge/threads',
        method: 'POST',
        body: args?.message ? { message: args.message } : {},
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      }),
      invalidatesTags: [{ type: attendee_match, id: 'CHATS' }],
    }),
    // a chat row's menu: rename, archive, restore
    updateAttendeeChat: build.mutation<{ data: Thread }, UpdateAttendeeChatRequest>({
      query: ({ id, ...body }) => ({
        url: `/attendee/agents/threads/${id}`,
        method: 'PATCH',
        body,
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      }),
      invalidatesTags: [{ type: attendee_match, id: 'CHATS' }],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // the thread's own title and status live in the ai_agent slice
          dispatch(aiAgentApi.util.invalidateTags([{ type: 'ai_agent', id: 'LIST' }]));
        } catch {
          // the mutation failed — nothing to refresh
        }
      },
    }),
  }),
});

export const {
  usePostMatchIntentMutation,
  useGetMatchesQuery,
  useGetMatchQuery,
  useLazyGetMatchQuery,
  useOpenMatchThreadMutation,
  useOpenConciergeThreadMutation,
  useOpenMatchingThreadMutation,
  useAnswerMatchingQuestionMutation,
  useSearchShortlistCompaniesQuery,
  useAddShortlistCompanyMutation,
  useChangeMatchStatusMutation,
  useGetAttendeeBriefQuery,
  useFillAttendeeBriefMutation,
  useGetAttendeeNotesQuery,
  useUpdateAttendeeNoteMutation,
  useDeleteAttendeeNoteMutation,
  useGetAttendeeChatsQuery,
  useGetMatchSlotsQuery,
  useBookMeetingMutation,
  useGetAttendeeMeetingsQuery,
  useChangeMeetingStatusMutation,
  useStartTopicChatMutation,
  useUpdateAttendeeChatMutation,
} = attendeeMatchApi;
